/**
 * Renders the confirm dialog fired from `confirmDialog()` in
 * `lib/confirmDialog.ts`. Mount once at the app root.
 *
 * Style parity with the Kora SweetAlert pattern (warning icon for
 * destructive flows, confirm + cancel buttons, single message line).
 * Implementation is a plain React modal portaled to document.body —
 * no sweetalert2 dependency.
 *
 * Accessibility:
 *   - Initial focus on the confirm button (or cancel for destructive).
 *   - Escape closes (= cancel).
 *   - Click on backdrop closes (= cancel).
 *   - Focus trap inside the dialog.
 *   - role="dialog" + aria-modal + aria-labelledby for screen readers.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { registerConfirmDialogHandler, type ConfirmDialogInput } from '@/lib/confirmDialog';
import { Button } from '@/components/ui/Button';

interface PendingDialog extends ConfirmDialogInput {
  resolve: (confirmed: boolean) => void;
}

export function ConfirmDialogHost() {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingDialog | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  // Singleton registration. Replaces any prior handler so a hot-reload
  // doesn't leak two hosts.
  useEffect(() => {
    registerConfirmDialogHandler((p) => setPending(p));
    return () => registerConfirmDialogHandler(null);
  }, []);

  const close = useCallback(
    (result: boolean) => {
      if (!pending) return;
      pending.resolve(result);
      setPending(null);
    },
    [pending],
  );

  // Initial focus + Escape handler. Destructive dialogs default-focus
  // Cancel so a stray Enter doesn't trigger a delete.
  useEffect(() => {
    if (!pending) return;
    const target = pending.destructive ? cancelBtnRef.current : confirmBtnRef.current;
    target?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(false);
      } else if (e.key === 'Enter' && !pending.destructive) {
        e.preventDefault();
        close(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pending, close]);

  if (!pending) return null;

  const confirmLabel = pending.confirmLabel ?? t('common.confirm');
  const cancelLabel = pending.cancelLabel ?? t('common.cancel');

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={pending.title ? 'confirm-dialog-title' : undefined}
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      onClick={(e) => {
        // Close on backdrop click, but not when clicking inside the
        // dialog (which sits in a child div with stopPropagation).
        if (e.target === e.currentTarget) close(false);
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="mk-card relative z-[1] w-full max-w-md overflow-hidden p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => close(false)}
          aria-label={cancelLabel}
          className="text-text-tertiary hover:bg-subtle hover:text-text-primary absolute top-3 right-3 rounded-md p-1 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3">
          {pending.destructive && (
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-500/15">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
          )}
          <div className="flex-1">
            {pending.title && (
              <h3
                id="confirm-dialog-title"
                className="font-heading text-text-primary text-lg font-bold"
              >
                {pending.title}
              </h3>
            )}
            <p className={`text-text-secondary text-sm ${pending.title ? 'mt-1' : ''}`}>
              {pending.message}
            </p>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button
            variant="ghost"
            ref={cancelBtnRef}
            onClick={() => close(false)}
            className="text-sm"
          >
            {cancelLabel}
          </Button>
          {/* Destructive confirms keep a custom solid-red recipe (stronger
              than mk-btn-danger's translucent tint) — deliberately a raw
              element, not a Button variant. */}
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={() => close(true)}
            className={
              pending.destructive
                ? 'rounded-xl bg-red-500/90 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-500'
                : 'mk-btn-primary text-sm'
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
